import {Component, OnDestroy, OnInit} from '@angular/core';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {ActivatedRoute} from '@angular/router';
import {takeUntil} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {ReplaySubject} from 'rxjs';
import {ContractService} from '../../../shared/services/contract.service';
import {Contract} from '../../../shared/interface/contract';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ListResponse} from '../../../shared/interface/response/list-response';
import {MatDialog} from '@angular/material/dialog';
import {DocumentAddDialogComponent} from '../../documents/document-add-dialog/document-add-dialog.component';
import {
  CONTRACT_STATUS_DURING_TERMINATION,
  CONTRACT_STATUS_TERMINATED,
  CONTRACT_STATUS_TO_TERMINATE,
  CONTRACT_TYPE_AGREEMENT_FOR_PARTNER_POINTS,
  CONTRACT_TYPE_FRANCHISE,
  CONTRACT_TYPE_FRANCHISE_AGREEMENT,
  CONTRACT_TYPE_LEAD,
  CONTRACT_TYPE_LEASING,
  CONTRACT_TYPE_OCK,
  CONTRACT_TYPE_SELF_BILLING,
  CONTRACT_TYPE_TERMINAL,
  FILE_TYPE_ANNEX,
  FILE_TYPE_ANNEX_AUTOMATIC_BILLING,
  FILE_TYPE_ANNEX_COMMISSION_ATTACHMENT,
  FILE_TYPE_ANNEX_LEAD,
  FILE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE,
  FILE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE_AND_AUTOMATIC_BILLING,
  FILE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE_WITHOUT_AUTOMATIC_BILLING,
  FILE_TYPE_ANNEX_OFWCA_APPLICATION,
  FILE_TYPE_CONFIDENTIALITY_AGREEMENT,
  FILE_TYPE_CONTRACT,
  FILE_TYPE_CONTRACT_TERMINATION,
  FILE_TYPE_DECLARATION_OF_CONSENT_TO_MAKE_COMMISSION_DEDUCTIONS,
  FILE_TYPE_OFWCA_APPLICATION,
  FILE_TYPE_RECEIPT_PROTOCOL,
  FILE_TYPE_STATEMENT_ON_THE_RIGHT_TO_DISPOSE_OF_REAL_ESTATE,
  INSURANCE_COMPANY_ANNEX_CONTRACT_TYPES,
  REGISTRATION_CONTRACT_TYPES
} from '../../../shared/services/dict.service';
import {Document} from '../../../shared/interface/document';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {
  RegistrationAddDialogComponent
} from '../../registrations/registration-add-dialog/registration-add-dialog.component';
import {
  ContractInsuranceCompaniesAddDialogComponent
} from '../contract-insurance-companies-add-dialog/contract-insurance-companies-add-dialog.component';

@Component({
  selector: 'app-contract-details',
  templateUrl: './contract-details.component.html',
  styleUrls: ['./contract-details.component.scss']
})
export class ContractDetailsComponent implements OnInit, OnDestroy {
  readonly CONTRACT_STATUS_TO_TERMINATE = CONTRACT_STATUS_TO_TERMINATE;
  readonly CONTRACT_STATUS_DURING_TERMINATION = CONTRACT_STATUS_DURING_TERMINATION;
  readonly CONTRACT_STATUS_TERMINATED = CONTRACT_STATUS_TERMINATED;
  readonly REGISTRATION_CONTRACT_TYPES = REGISTRATION_CONTRACT_TYPES;
  readonly INSURANCE_COMPANY_ANNEX_CONTRACT_TYPES = INSURANCE_COMPANY_ANNEX_CONTRACT_TYPES;
  readonly CONTRACT_TYPE_AGREEMENT_FOR_PARTNER_POINTS = CONTRACT_TYPE_AGREEMENT_FOR_PARTNER_POINTS;
  readonly CONTRACT_TYPE_FRANCHISE_AGREEMENT = CONTRACT_TYPE_FRANCHISE_AGREEMENT;
  readonly CONTRACT_TYPE_FRANCHISE = CONTRACT_TYPE_FRANCHISE;
  readonly CONTRACT_TYPE_LEAD = CONTRACT_TYPE_LEAD;
  readonly CONTRACT_TYPE_OCK = CONTRACT_TYPE_OCK;
  readonly CONTRACT_TYPE_SELF_BILLING = CONTRACT_TYPE_SELF_BILLING;
  readonly CONTRACT_TYPE_LEASING = CONTRACT_TYPE_LEASING;
  readonly CONTRACT_TYPE_TERMINAL = CONTRACT_TYPE_TERMINAL;

  contractId: string;
  contractObject?: Contract;
  contract: ReplaySubject<Contract> = new ReplaySubject<Contract>(1);
  documents: ReplaySubject<Document[]> = new ReplaySubject<Document[]>(1);
  registrationsChanged: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  modify = false;
  userPermissions: ReplaySubject<UserPermissions>;
  reloadInsuranceCompanyList: ReplaySubject<any> = new ReplaySubject<any>(1);
  selectedTab: number;
  processingRequest = false;
  editBlock = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private route: ActivatedRoute,
    private contractService: ContractService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
  ) {
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroyed$))
      .subscribe(params => {
        this.contractId = params.id;
        this.selectedTab = 0;
        this.loadContract();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadDocuments(): void {
    this.contractService.getContractDocuments(this.contractId)
      .subscribe((documents: ListResponse<Document>) => {
        this.documents.next(documents.items);
      });
  }

  onTabChange(e: MatTabChangeEvent): void {
    if (e.index === 2) {
      this.loadDocuments();
    }
  }

  addDocument(): void {
    const dialogRef = this.dialog.open(DocumentAddDialogComponent, {
      width: '55%',
      data: {
        contractObject: this.contractObject,
        types: [
          FILE_TYPE_CONTRACT_TERMINATION,
          FILE_TYPE_CONTRACT,
          FILE_TYPE_ANNEX,
          FILE_TYPE_OFWCA_APPLICATION,
          FILE_TYPE_ANNEX_OFWCA_APPLICATION,
          FILE_TYPE_ANNEX_COMMISSION_ATTACHMENT,
          FILE_TYPE_CONFIDENTIALITY_AGREEMENT,
          FILE_TYPE_STATEMENT_ON_THE_RIGHT_TO_DISPOSE_OF_REAL_ESTATE,
          FILE_TYPE_DECLARATION_OF_CONSENT_TO_MAKE_COMMISSION_DEDUCTIONS,
          FILE_TYPE_RECEIPT_PROTOCOL,
          FILE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE,
          FILE_TYPE_ANNEX_LEAD,
          FILE_TYPE_ANNEX_AUTOMATIC_BILLING,
          FILE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE_AND_AUTOMATIC_BILLING,
          FILE_TYPE_ANNEX_NEW_CONTRACT_TEMPLATE_WITHOUT_AUTOMATIC_BILLING
        ],
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.editBlock = true;
      this.loadContract();
      this.loadDocuments();
    });
  }

  loadContract(): void {
    this.contractService.get(this.contractId).subscribe((contract: Contract) => {
      this.contract.next(contract);
      this.contractObject = contract;
      this.editBlock = false;
    }, (error: HttpErrorResponse) => {
      this.snack.open('Nie udało się pobrać danych umowy', error.status.toString(), {
        duration: 2000
      });
    });
  }

  addRegistration(): void {
    const dialogRef = this.dialog.open(RegistrationAddDialogComponent, {
      width: '90vw',
      maxHeight: '90vh',
      data: {
        contractObject: this.contractObject,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.registrationsChanged.next(true);
    });
  }

  addInsuranceCompany(): void {
    const dialogRef = this.dialog.open(ContractInsuranceCompaniesAddDialogComponent, {
      width: '90vw',
      maxHeight: '90vh',
      data: {
        contractObject: this.contractObject,
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadContract();
      this.reloadInsuranceCompanyList.next();
    });
  }

  updateAllAnnexes(field: string): void {
    this.processingRequest = true;

    this.contractService.updateAllContractAnnexes(this.contractId, field)
      .subscribe({
        next: () => {
          this.processingRequest = false;
          this.snack.open('Załączniki zostały zaktualizowane', '', {duration: 2000});
          this.reloadInsuranceCompanyList.next();
        },
        error: () => {
          this.processingRequest = false;
          this.snack.open('Przy aktualizacji wystąpił błąd', '', {duration: 2000});
        }
      });
  }

}
